import { useEditor, EditorContent } from '@tiptap/react'
import { StarterKit } from '@tiptap/starter-kit'
import { TextStyle, Color, FontSize } from '@tiptap/extension-text-style'
import { Underline } from '@tiptap/extension-underline'
import { Link } from '@tiptap/extension-link'
import { TextAlign } from '@tiptap/extension-text-align'
import { Bold, Italic, Underline as UnderlineIcon, Link2, AlignLeft, AlignCenter, AlignRight, AlignJustify, WrapText, Highlighter } from 'lucide-react'
import { useEffect, useState } from 'react'

const FONT_SIZES = ['11px', '13px', '15px', '18px', '22px', '28px', '36px']
const COLORS = ['#ffffff', '#000000', '#e30203', '#ff4444', '#ffaa00', '#00cc44', '#0099ff', '#a855f7', '#ff00ff']
export function RichTextEditor({ value, onChange, outlineColor, onOutlineChange }: { value: string; onChange: (html: string) => void; outlineColor?: string; onOutlineChange?: (c: string) => void }) {
  const [fontSize, setFontSize] = useState('15px')
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        hardBreak: { keepMarks: true },
      }),
      TextStyle,
      Color,
      FontSize,
      Underline,
      Link.configure({ openOnClick: false, HTMLAttributes: { class: 'underline' } }),
      TextAlign.configure({ types: ['heading', 'paragraph'], defaultAlignment: 'center' }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        class: 'min-h-[80px] p-3 focus:outline-none text-sm text-white',
        style: 'word-break: break-word; line-height: 1.5;',
      },
    },
  })

  useEffect(() => {
    if (editor && value !== editor.getHTML()) editor.commands.setContent(value || '')
  }, [value, editor])

  if (!editor) return null

  const setColor = (color: string) => editor.chain().focus().setColor(color).run()
  const applyFontSize = (size: string) => {
    setFontSize(size)
    ;(editor.chain().focus() as any).setFontSize(size).run()
  }
  const setLink = () => {
    const url = window.prompt('URL (deixe vazio pra remover)')
    if (url === null) return
    if (url === '') editor.chain().focus().extendMarkRange('link').unsetLink().run()
    else editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  }
  const applyOutline = (color: string) => {
    onOutlineChange?.(color)
  }
  const insertLineBreak = () => {
    editor.chain().focus().setHardBreak().run()
  }

  const btnClass = (active: boolean) => `p-1.5 rounded hover:bg-muted/50 transition-colors ${active ? 'bg-primary/20 text-primary' : 'text-foreground'}`

  return (
    <div className="rounded-md border border-border bg-background overflow-hidden">
      {/* Toolbar row 1: text formatting */}
      <div className="flex flex-wrap gap-1 items-center border-b border-border p-2">
        <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} className={btnClass(editor.isActive('bold'))} title="Negrito (Ctrl+B)">
          <Bold className="h-3.5 w-3.5" />
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} className={btnClass(editor.isActive('italic'))} title="Itálico (Ctrl+I)">
          <Italic className="h-3.5 w-3.5" />
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleUnderline().run()} className={btnClass(editor.isActive('underline'))} title="Sublinhado (Ctrl+U)">
          <UnderlineIcon className="h-3.5 w-3.5" />
        </button>
        <div className="w-px h-5 bg-border mx-0.5" />
        <select value={fontSize} onChange={(e) => applyFontSize(e.target.value)} className="text-xs bg-background border border-border rounded px-1 py-1 h-7" title="Tamanho da fonte">
          {FONT_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <div className="w-px h-5 bg-border mx-0.5" />
        <button type="button" onClick={setLink} className={btnClass(editor.isActive('link'))} title="Inserir link">
          <Link2 className="h-3.5 w-3.5" />
        </button>
        <button type="button" onClick={insertLineBreak} className={btnClass(false)} title="Quebra de linha (Shift+Enter)">
          <WrapText className="h-3.5 w-3.5" />
        </button>
        <div className="w-px h-5 bg-border mx-0.5" />
        <button type="button" onClick={() => editor.chain().focus().setTextAlign('left').run()} className={btnClass(editor.isActive({ textAlign: 'left' }))} title="Esquerda">
          <AlignLeft className="h-3.5 w-3.5" />
        </button>
        <button type="button" onClick={() => editor.chain().focus().setTextAlign('center').run()} className={btnClass(editor.isActive({ textAlign: 'center' }) || (!editor.isActive({ textAlign: 'left' }) && !editor.isActive({ textAlign: 'right' }) && !editor.isActive({ textAlign: 'justify' })))} title="Centro">
          <AlignCenter className="h-3.5 w-3.5" />
        </button>
        <button type="button" onClick={() => editor.chain().focus().setTextAlign('right').run()} className={btnClass(editor.isActive({ textAlign: 'right' }))} title="Direita">
          <AlignRight className="h-3.5 w-3.5" />
        </button>
        <button type="button" onClick={() => editor.chain().focus().setTextAlign('justify').run()} className={btnClass(editor.isActive({ textAlign: 'justify' }))} title="Justificar">
          <AlignJustify className="h-3.5 w-3.5" />
        </button>
      </div>
      {/* Toolbar row 2: colors + outline */}
      <div className="flex flex-wrap gap-1 items-center border-b border-border p-2">
        <span className="text-[10px] text-muted-foreground mr-1">Cor:</span>
        {COLORS.map(c => (
          <button key={c} type="button" onClick={() => setColor(c)} className="w-5 h-5 rounded border border-border hover:scale-110 transition-transform" style={{ background: c }} />
        ))}
        <input type="color" onChange={(e) => setColor(e.target.value)} className="w-5 h-5 rounded cursor-pointer border border-border" title="Cor personalizada" />
        <div className="w-px h-5 bg-border mx-1" />
        <span className="text-[10px] text-muted-foreground mr-1">Efeito:</span>
        <select value={outlineColor || 'none'} onChange={(e) => applyOutline(e.target.value)} className="text-xs bg-background border border-border rounded px-1 py-0.5 h-6">
          <option value="none">Nenhum</option>
          <optgroup label="Contorno">
            <option value="outline-black">Contorno preto</option>
            <option value="outline-white">Contorno branco</option>
            <option value="outline-red">Contorno vermelho</option>
          </optgroup>
          <optgroup label="Sombra projetada">
            <option value="shadow-black">Sombra preta</option>
            <option value="shadow-white">Sombra branca</option>
            <option value="shadow-red">Sombra vermelha</option>
            <option value="shadow-blue">Sombra azul</option>
          </optgroup>
          <optgroup label="Glow (brilho)">
            <option value="glow-red">Glow vermelho</option>
            <option value="glow-blue">Glow azul</option>
            <option value="glow-white">Glow branco</option>
          </optgroup>
        </select>
      </div>
      {/* Editor content */}
      <div className="bg-zinc-900 rounded-b-md">
        <EditorContent editor={editor} />
      </div>
    </div>
  )
}
